import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Link, IconButton, TextField, MenuItem, Tooltip, Switch, FormControlLabel, FormHelperText } from '@mui/material';
import styled from '@emotion/styled';
import { useHasPermission } from '../utils/permissionUtils'
import { useIsLocked } from '../utils/lockUtils';
import { useDispatch } from 'react-redux';
import { updateClient, activateClient, deactivateClient } from '../redux/ducks/client';
import { setTagAssignments } from '../redux/ducks/tag';
import TagPicker, { useTagDraft } from './TagPicker';
import { Edit, Check, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ActiveChip from './ActiveChip';
import PrivacyDropdown from './PrivacyDropdown';
import { statusColor } from '../utils/statusColors';
import { sanitizeUrl } from '../utils/navigateUtils';

const isSafeWebsite = (value) => {
    if (!value) return true;
    try {
        const { protocol } = new URL(value);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return true;
    }
};

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    overflowWrap: 'anywhere',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const ContainerBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.paper,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(4),
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(5),
    },
}))

const EditBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}))

const PrivacyDot = styled(Box)(({ theme }) => ({
    width: theme.spacing(1.5),
    minWidth: theme.spacing(1.5),
    height: theme.spacing(1.5),
    borderRadius: '50%',
}))

const CompactFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginLeft: 0,
    '& .MuiFormControlLabel-label': {
        whiteSpace: 'nowrap',
    },
}))

const SwitchHint = styled(FormHelperText)({
    margin: 0,
})

const ClientInfoGridBox = (client) => {
    const { clientData } = client;
    const [currentName, setCurrentName] = useState(clientData.name);
    const [currentAddress, setCurrentAddress] = useState(clientData.address);
    const [currentPhone, setCurrentPhone] = useState(clientData.phone);
    const [currentWebsite, setCurrentWebsite] = useState(clientData.website);
    const [currentActive, setCurrentActive] = useState(clientData.active);
    const [currentBillingTerms, setCurrentBillingTerms] = useState(clientData.billingTerms);
    const [currentPrivacy, setCurrentPrivacy] = useState(clientData.privacy);
    const [currentTags, setCurrentTags] = useState(clientData.tags || []);
    const [editedName, setEditedName] = useState(clientData.name);
    const [editedAddress, setEditedAddress] = useState(clientData.address);
    const [editedPhone, setEditedPhone] = useState(clientData.phone);
    const [editedWebsite, setEditedWebsite] = useState(clientData.website);
    const [editedActive, setEditedActive] = useState(clientData.active);
    const [editedBillingTerms, setEditedBillingTerms] = useState(clientData.billingTerms);
    const [editedPrivacy, setEditedPrivacy] = useState(clientData.privacy);
    const [isEditing, setIsEditing] = useState(false);
    const [tagsSaving, setTagsSaving] = useState(false);
    const [currentAutoEscalate, setCurrentAutoEscalate] = useState(!!clientData.autoEscalateUnansweredComments);
    const [editedAutoEscalate, setEditedAutoEscalate] = useState(!!clientData.autoEscalateUnansweredComments);
    const [currentRequireTags, setCurrentRequireTags] = useState(!!clientData.requireProjectTags);
    const [editedRequireTags, setEditedRequireTags] = useState(!!clientData.requireProjectTags);
    const theme = useTheme();
    const hasPermission = useHasPermission();
    const canEditClient = hasPermission("editClient");
    const canEditOwnClient = hasPermission("editOwnClient");
    const canEditBillingTerms = hasPermission("editBillingTerms");
    const canSeeInternal = hasPermission("canSeeInternal");
    const isLocked = useIsLocked(clientData, null);
    const tagsEditable = isEditing && !clientData.isDeleted;
    const tagDraft = useTagDraft({ enabled: tagsEditable, targetType: 'Client', targetId: clientData._id, tags: currentTags });
    const dispatch = useDispatch();

    useEffect(() => {
        setCurrentName(clientData.name);
        setCurrentAddress(clientData.address);
        setCurrentPhone(clientData.phone);
        setCurrentWebsite(clientData.website);
        setCurrentActive(clientData.active);
        setCurrentBillingTerms(clientData.billingTerms);
        setCurrentPrivacy(clientData.privacy);
        setCurrentAutoEscalate(!!clientData.autoEscalateUnansweredComments);
        setCurrentRequireTags(!!clientData.requireProjectTags);
        setCurrentTags(clientData.tags || []);
    }, [clientData]);

    const handleEdit = () => {
        setEditedName(currentName);
        setEditedAddress(currentAddress);
        setEditedPhone(currentPhone);
        setEditedWebsite(currentWebsite);
        setEditedActive(currentActive);
        setEditedBillingTerms(currentBillingTerms);
        setEditedPrivacy(currentPrivacy);
        setEditedAutoEscalate(currentAutoEscalate);
        setEditedRequireTags(currentRequireTags);
        setIsEditing(true);
    }

    const handleSave = () => {
        if (!isSafeWebsite(editedWebsite)) {
            return;
        }
        dispatch(
            updateClient(clientData._id, {
            address: editedAddress,
            phone: editedPhone,
            website: editedWebsite,
            ...(canEditClient ? { name: editedName, billingTerms: editedBillingTerms, privacy: editedPrivacy, requireProjectTags: editedRequireTags } : {}),
            ...(canSeeInternal ? { autoEscalateUnansweredComments: editedAutoEscalate } : {})
        }, (response) => {
            if (response?.client) {
                    setCurrentName(response.client.name);
                    setCurrentAddress(response.client.address);
                    setCurrentPhone(response.client.phone);
                    setCurrentWebsite(response.client.website);
                    setCurrentActive(response.client.active);
                    setCurrentBillingTerms(response.client.billingTerms);
                    setCurrentPrivacy(response.client.privacy);
                    setCurrentAutoEscalate(!!response.client.autoEscalateUnansweredComments);
                    setCurrentRequireTags(!!response.client.requireProjectTags);
                }
            })
        );
        const saveTags = tagsEditable && tagDraft.dirty;
        if (saveTags) {
            setTagsSaving(true);
            dispatch(setTagAssignments('Client', clientData._id, tagDraft.assignments, (data) => {
                setTagsSaving(false);
                if (!data) return;
                setCurrentTags(data.tags || []);
                setIsEditing(false);
            }));
        }
        if (currentActive !== editedActive) {
            dispatch(editedActive ? activateClient(clientData._id, (response) => {
                if (response.status === 1) {
                    setCurrentActive(true);
                }
            }) : deactivateClient(clientData._id, (response) => {
                if (response.status === 1) {
                    setCurrentActive(false);
                }
            }));
        }
        if (!saveTags) setIsEditing(false);
    }

    const handleCancel = () => {
        setIsEditing(false);
        setEditedName(currentName);
        setEditedAddress(currentAddress);
        setEditedPhone(currentPhone);
        setEditedWebsite(currentWebsite);
        setEditedActive(currentActive);
        setEditedBillingTerms(currentBillingTerms);
        setEditedPrivacy(currentPrivacy);
        setEditedAutoEscalate(currentAutoEscalate);
        setEditedRequireTags(currentRequireTags);
    }

    return (
        <ContainerBox>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                    {currentPrivacy && currentPrivacy !== "Public" && (
                        <Tooltip title={currentPrivacy}>
                            <PrivacyDot bgcolor={statusColor(theme, "privacy", currentPrivacy)} />
                        </Tooltip>
                    )}
                    <Typography variant="h6" fontWeight="bold">{currentName}</Typography>
                    {!currentActive && <ActiveChip status="Inactive" />}
                </Box>
                {(canEditClient || canEditOwnClient) && !isLocked && (
                    isEditing ?
                        <Box display="flex" gap={2}>
                            <IconButton onClick={handleSave} disabled={tagDraft.loading || tagsSaving}><Check /></IconButton>
                            <IconButton onClick={handleCancel}><Close /></IconButton>
                        </Box>
                        :
                        <IconButton onClick={handleEdit}><Edit /></IconButton>
                )}
            </Box>
            <Grid container spacing={{ xs: 2, md: 3, lg: 4 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Address</Typography>
                    {isEditing ? (
                        <EditBox>
                            <TextField label="Street" value={editedAddress.street} onChange={(e) => setEditedAddress({ ...editedAddress, street: e.target.value })} fullWidth />
                            <TextField label="City" value={editedAddress.city} onChange={(e) => setEditedAddress({ ...editedAddress, city: e.target.value })} fullWidth />
                            <TextField label="State" value={editedAddress.state} onChange={(e) => setEditedAddress({ ...editedAddress, state: e.target.value })} fullWidth />
                            <TextField label="Postal Code" value={editedAddress.postalCode} onChange={(e) => setEditedAddress({ ...editedAddress, postalCode: e.target.value })} fullWidth />
                        </EditBox>
                    ) : (
                        <Typography>{currentAddress?.street}<br />{currentAddress?.city}, {currentAddress?.state} {currentAddress?.postalCode}</Typography>
                    )}
                </Grid>
                    <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Phone</Typography>
                    {isEditing ? (
                        <TextField value={editedPhone} onChange={(e) => setEditedPhone(e.target.value)} fullWidth />
                    ) : (
                        <StyledLink href={`tel:${currentPhone}`}>{currentPhone}</StyledLink>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Website</Typography>
                    {isEditing ? (
                        <TextField
                            value={editedWebsite}
                            onChange={(e) => setEditedWebsite(e.target.value)}
                            fullWidth
                            error={!isSafeWebsite(editedWebsite)}
                            helperText={!isSafeWebsite(editedWebsite) ? "Enter a valid http:// or https:// URL" : ""}
                        />
                    ) : (
                        <StyledLink href={sanitizeUrl(currentWebsite)} target="_blank" color="primary" underline="hover">{currentWebsite}</StyledLink>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    {canEditClient && !isLocked && isEditing ? (
                        <TextField value={editedActive} onChange={(e) => setEditedActive(e.target.value)} fullWidth select>
                            <MenuItem value={true}>Active</MenuItem>
                            <MenuItem value={false}>Inactive</MenuItem>
                        </TextField>
                    ) : (
                        <Box display="flex" alignItems="center">
                            <ActiveChip status={currentActive ? "Active" : "Inactive"} />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Billing Type</Typography>
                    {canEditBillingTerms && canEditClient && !isLocked && isEditing ? (
                        <TextField value={editedBillingTerms.type} onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, type: e.target.value })} fullWidth select>
                            <MenuItem value="Hourly">Hourly</MenuItem>
                            <MenuItem value="Retainer">Retainer</MenuItem>
                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                        </TextField>
                    ) : (
                        <Typography>{currentBillingTerms?.type}</Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Terms</Typography>
                    {canEditBillingTerms && canEditClient && !isLocked && isEditing ? (
                        editedBillingTerms?.type === 'Retainer' &&
                        <EditBox>
                                <TextField label="Hours" type="number" value={editedBillingTerms.hours} onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, hours: e.target.value })} fullWidth />
                                <TextField label="Frequency" value={editedBillingTerms.frequency} onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, frequency: e.target.value })} fullWidth select>
                                    <MenuItem value="Monthly">Monthly</MenuItem>
                                    <MenuItem value="Weekly">Weekly</MenuItem>
                                </TextField>
                                <TextField label="Overage" value={editedBillingTerms.overage} onChange={(e) => setEditedBillingTerms({ ...editedBillingTerms, overage: e.target.value })} fullWidth select>
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </TextField>
                        </EditBox>
                    ):(
                        currentBillingTerms?.type === 'Retainer' &&
                            <Typography>{currentBillingTerms?.hours} Hrs {currentBillingTerms?.frequency} / {currentBillingTerms?.overage ? 'Overage Allowed' : 'No Overage'}</Typography>
                    )}

                </Grid>
                {canEditClient && !isLocked && isEditing && (
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">Privacy</Typography>
                        <PrivacyDropdown value={editedPrivacy} onChange={(e) => setEditedPrivacy(e.target.value)} />
                    </Grid>
                )}
                {(tagsEditable || currentTags.length > 0) && (
                    <Grid item xs={12}>
                        {tagsEditable ? (
                            <TagPicker draft={tagDraft} targetType="Client" targetId={clientData._id} clientId={clientData._id} clientName={clientData.name} />
                        ) : (
                            <TagPicker value={currentTags} targetType="Client" targetId={clientData._id} readOnly />
                        )}
                    </Grid>
                )}
                {canEditClient && !isLocked && isEditing && (
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">Project Tags</Typography>
                        <CompactFormControlLabel
                            control={<Switch checked={editedRequireTags} onChange={(e) => setEditedRequireTags(e.target.checked)} inputProps={{ "aria-describedby": "require-project-tags-hint" }} />}
                            label="Require tags on new projects"
                        />
                        <SwitchHint id="require-project-tags-hint">New projects need at least one tag. Tags inherited from the client, account or group count.</SwitchHint>
                    </Grid>
                )}
                {isEditing && canSeeInternal && (
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">Comment Escalation</Typography>
                        <Tooltip title="When enabled, unanswered client comments on this client's projects get automatic public acknowledgements and escalation.">
                            <CompactFormControlLabel
                                control={<Switch checked={editedAutoEscalate} onChange={(e) => setEditedAutoEscalate(e.target.checked)} />}
                                label="Auto-escalate comments"
                            />
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
        </ContainerBox>
    )
}

export default ClientInfoGridBox;